import { AmbientMode } from './ambient';
import { AccessoryId, FACE_H, FACE_STATE_NAMES, FACE_W, FaceState, RenderStyle, STATE_PARAMS } from './face';
import { paletteForFace, type ResolvedFacePalette } from './palette';
import { PalForm, PalMark } from './types';

export type CartoonEyeStyle = 'normal' | 'closed' | 'x' | 'spiral' | 'dollar' | 'star' | 'happy';
export type AmbientGlyphKind = 'note' | 'heart' | 'sparkle' | 'alert' | 'snow' | 'sleep';

export interface CartoonEye {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
    pupilCx: number;
    pupilCy: number;
    pupilR: number;
    style: CartoonEyeStyle;
}

export interface CartoonMouth {
    d: string;
    open: boolean;
    strokeWidth: number;
}

export interface AmbientGlyph {
    kind: AmbientGlyphKind;
    x: number;
    y: number;
    scale: number;
    opacity: number;
    color?: string;
}

export interface CartoonShapes {
    width: number;
    height: number;
    stateName: string;
    palette: ResolvedFacePalette;
    aura: boolean;
    leftEye: CartoonEye;
    rightEye: CartoonEye;
    mouth: CartoonMouth;
    cheeks: Array<{ cx: number; cy: number; r: number; opacity: number }>;
    glyphs: AmbientGlyph[];
    markGlyphs: AmbientGlyph[];
    accessory: AccessoryId;
}

export interface BuildCartoonShapesInput {
    state: FaceState;
    ambient: AmbientMode;
    form: PalForm;
    marks: number;
    accessory: AccessoryId;
    nowMs: number;
}

const EYE_LEFT_CX = 44;
const EYE_RIGHT_CX = 84;
const EYE_CY = 28;
const MOUTH_CX = 64;
const MOUTH_CY = 50;

export function buildCartoonShapes(input: BuildCartoonShapesInput): CartoonShapes {
    const params = STATE_PARAMS[input.state];
    const palette = paletteForFace(input.form, input.state);
    const eyeStyle = eyeStyleFor(params.style);
    const eyeY = EYE_CY + params.pupilY * 3;
    const rx = eyeStyle === 'closed' ? 8 : 10;
    const ry = eyeStyle === 'closed' ? 1 : Math.max(4, 10 - params.eyebrowsLow * 4);
    const pupilR = 2.5 + params.pupilSize * 4;
    const pupilOffsetX = params.pupilX * 4;
    const pupilOffsetY = params.pupilY * 2;

    return {
        width: FACE_W,
        height: FACE_H,
        stateName: FACE_STATE_NAMES[input.state],
        palette,
        aura: input.ambient === AmbientMode.AURA || input.form === PalForm.CalmExplorer,
        leftEye: eye(EYE_LEFT_CX, eyeY, rx, ry, pupilR, pupilOffsetX, pupilOffsetY, eyeStyle),
        rightEye: eye(EYE_RIGHT_CX, eyeY, rx, ry, pupilR, pupilOffsetX, pupilOffsetY, eyeStyle),
        mouth: buildMouth(params.mouthCurve, params.drawOpen, params.drawClosed),
        cheeks: buildCheeks(params.mouthCurve),
        glyphs: buildAmbientGlyphs(input.ambient, input.state, input.nowMs),
        markGlyphs: buildMarkGlyphs(input.marks, input.nowMs),
        accessory: input.accessory,
    };
}

function eye(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    pupilR: number,
    pupilOffsetX: number,
    pupilOffsetY: number,
    style: CartoonEyeStyle,
): CartoonEye {
    return {
        cx,
        cy,
        rx,
        ry,
        pupilCx: cx + pupilOffsetX,
        pupilCy: cy + pupilOffsetY,
        pupilR,
        style,
    };
}

function eyeStyleFor(style: RenderStyle): CartoonEyeStyle {
    switch (style) {
        case RenderStyle.CLOSED:
        case RenderStyle.ZZZ_SLEEPY:
            return 'closed';
        case RenderStyle.X_EYES:
            return 'x';
        case RenderStyle.SPIRAL_EYES:
            return 'spiral';
        case RenderStyle.DOLLAR_EYES:
            return 'dollar';
        case RenderStyle.STAR_EYES:
            return 'star';
        case RenderStyle.SMILE_FACE:
            return 'happy';
        default:
            return 'normal';
    }
}

function buildMouth(curve: number, open: boolean, closed: boolean): CartoonMouth {
    if (closed) {
        return { d: `M ${MOUTH_CX - 8} ${MOUTH_CY} L ${MOUTH_CX + 8} ${MOUTH_CY}`, open: false, strokeWidth: 2 };
    }
    const controlY = MOUTH_CY - curve;
    const openTail = open && curve > 6 ? ` Q ${MOUTH_CX} ${MOUTH_CY + 6} ${MOUTH_CX + 10} ${MOUTH_CY}` : '';
    return {
        d: `M ${MOUTH_CX - 10} ${MOUTH_CY} Q ${MOUTH_CX} ${controlY} ${MOUTH_CX + 10} ${MOUTH_CY}${openTail}`,
        open: open && curve > 6,
        strokeWidth: Math.abs(curve) > 6 ? 3 : 2,
    };
}

function buildCheeks(curve: number): CartoonShapes['cheeks'] {
    if (curve < 4) return [];
    return [
        { cx: 30, cy: 42, r: 4, opacity: 0.34 },
        { cx: 98, cy: 42, r: 4, opacity: 0.34 },
    ];
}

function buildAmbientGlyphs(ambient: AmbientMode, state: FaceState, nowMs: number): AmbientGlyph[] {
    const phase = nowMs / 1000;
    const glyphs: AmbientGlyph[] = [];

    if (ambient === AmbientMode.NOTES) {
        glyphs.push(...floatGlyphs('note', phase, [{ x: 20, y: 18 }, { x: 108, y: 22 }, { x: 68, y: 10 }]));
    }
    if (ambient === AmbientMode.HEARTS) {
        glyphs.push(...floatGlyphs('heart', phase, [{ x: 18, y: 42 }, { x: 108, y: 40 }, { x: 64, y: 12 }]));
    }
    if (ambient === AmbientMode.SPARKLE) {
        glyphs.push(...floatGlyphs('sparkle', phase, [{ x: 15, y: 12 }, { x: 112, y: 14 }, { x: 64, y: 7 }]));
    }
    if (ambient === AmbientMode.ALERT) {
        glyphs.push(...floatGlyphs('alert', phase, [{ x: 14, y: 18 }, { x: 113, y: 18 }]));
    }
    if (ambient === AmbientMode.MIC) {
        glyphs.push(...floatGlyphs('sparkle', phase, [
            { x: 20, y: 16 },
            { x: 108, y: 16 },
            { x: 26, y: 48 },
            { x: 102, y: 48 },
        ]).map((glyph) => ({ ...glyph, color: '#67e8f9' })));
    }

    if (state === FaceState.COLD) {
        glyphs.push(...floatGlyphs('snow', phase, [{ x: 12, y: 10 }, { x: 116, y: 12 }]));
    }
    if (state === FaceState.SLEEPING) {
        glyphs.push(...floatGlyphs('sleep', phase, [{ x: 94, y: 14 }, { x: 105, y: 9 }]));
    }
    if (state === FaceState.SCARED || state === FaceState.CHECK_ENGINE) {
        glyphs.push(...floatGlyphs('alert', phase, [{ x: 12, y: 14 }, { x: 116, y: 14 }]));
    }

    return glyphs;
}

function buildMarkGlyphs(marks: number, nowMs: number): AmbientGlyph[] {
    const glyphs: AmbientGlyph[] = [];
    if (marks & PalMark.SmoothDriveWeek) {
        glyphs.push({ kind: 'sparkle', x: 8, y: 8 + Math.sin(nowMs / 700) * 1.5, scale: 0.8, opacity: 0.9 });
    }
    if (marks & PalMark.NightCruiserStreak) {
        glyphs.push({ kind: 'sleep', x: 118, y: 9, scale: 0.75, opacity: 0.84 });
    }
    return glyphs;
}

function floatGlyphs(
    kind: AmbientGlyphKind,
    phase: number,
    points: Array<{ x: number; y: number }>,
): AmbientGlyph[] {
    return points.map((p, i) => ({
        kind,
        x: p.x + Math.sin(phase + i) * 1.7,
        y: p.y + Math.cos(phase * 0.8 + i) * 3,
        scale: 0.82 + (i % 2) * 0.18,
        opacity: 0.62 + (Math.sin(phase + i * 0.7) + 1) * 0.18,
    }));
}
